import {
  ArrowBack,
  DeleteOutline,
  Email,
  Gavel,
  Shield,
} from "@mui/icons-material";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import type React from "react";
import { Link as RouterLink } from "react-router-dom";

type LegalPageKind = "privacy" | "terms" | "data-deletion";

type LegalSection = {
  title: string;
  body: string[];
};

type LegalContent = {
  eyebrow: string;
  title: string;
  updatedAt: string;
  intro: string;
  icon: React.ReactNode;
  sections: LegalSection[];
};

const contactEmail = "contacto@lyproai.com";
const updatedAt = "16 de julio de 2026";

const legalContent: Record<LegalPageKind, LegalContent> = {
  privacy: {
    eyebrow: "Privacidad",
    title: "Politica de privacidad",
    updatedAt,
    intro:
      "Esta politica explica como LyPro AI recolecta, usa, almacena y protege la información relacionada con sus usuarios, clientes y conversaciones gestionadas por la plataforma.",
    icon: <Shield />,
    sections: [
      {
        title: "Información que recolectamos",
        body: [
          "Podemos recolectar datos de cuenta como nombre, correo electrónico, identificadores de empresa, rol de usuario y credenciales de acceso gestionadas por proveedores de autenticacion.",
          "Cuando una empresa conecta canales como WhatsApp, Facebook o Instagram, podemos almacenar identificadores técnicos necesarios para operar la integración, como IDs de cuenta, IDs de número, tokens de acceso, estado de conexión y metadatos asociados.",
          "La plataforma puede procesar mensajes, archivos, datos de contacto, pedidos, reservas, facturación, direcciones o información comercial que los usuarios finales compartan durante una conversación.",
        ],
      },
      {
        title: "Cómo usamos la información",
        body: [
          "Usamos la información para prestar el servicio, autenticar usuarios, operar asistentes conversacionales, enrutar mensajes, generar respuestas, registrar pedidos o reservas, entregar soporte y mostrar analíticas operativas.",
          "También podemos usar datos técnicos para prevenir abuso, diagnosticar errores, mejorar seguridad, mantener integraciones y cumplir obligaciones legales o contractuales.",
        ],
      },
      {
        title: "Servicios de terceros",
        body: [
          "LyPro AI puede integrarse con proveedores externos como Meta, WhatsApp, Facebook, Instagram, Firebase, Google Cloud, OpenAI y sistemas operativos o comerciales conectados por cada empresa.",
          "Cada integración se usa solo en la medida necesaria para entregar las funciones solicitadas por el cliente o para mantener la operacion técnica de la plataforma.",
        ],
      },
      {
        title: "Conservacíon y seguridad",
        body: [
          "Conservamos la información mientras sea necesaria para prestar el servicio, cumplir obligaciones legales, resolver disputas, mantener auditoria operativa o atender solicitudes de soporte.",
          "Aplicamos controles razonables de seguridad, acceso limitado, almacenamiento protegido y practicas de monitoreo para reducir riesgos de perdida, mal uso o acceso no autorizado.",
        ],
      },
      {
        title: "Derechos y solicitudes",
        body: [
          "Los usuarios pueden solicitar acceso, correccion, actualizacion o eliminacion de sus datos personales cuando corresponda segun la ley aplicable.",
          `Para ejercer estos derechos, escribe a ${contactEmail} indicando la empresa asociada, el canal usado y el alcance de la solicitud.`,
        ],
      },
    ],
  },
  terms: {
    eyebrow: "Condiciones",
    title: "Condiciones del servicio",
    updatedAt,
    intro:
      "Estas condiciones regulan el acceso y uso de LyPro AI, una plataforma para gestionar conversaciones, automatizaciones, integraciones y operaciones comerciales asistidas por inteligencia artificial.",
    icon: <Gavel />,
    sections: [
      {
        title: "Uso del servicio",
        body: [
          "El usuario debe usar LyPro AI de forma legal, responsable y conforme a las políticas de los canales e integraciones conectadas, incluyendo las políticas de Meta, WhatsApp, Facebook e Instagram cuando apliquen.",
          "Cada empresa es responsable de la información que carga, de las instrucciones que configura para sus asistentes y de contar con autorización para tratar los datos de sus clientes o usuarios finales.",
        ],
      },
      {
        title: "Cuentas y acceso",
        body: [
          "El acceso al panel esta reservado a usuarios autorizados. El usuario debe mantener sus credenciales protegidas y reportar cualquier acceso no autorizado.",
          "LyPro AI puede suspender o limitar accesos si detecta abuso, riesgo de seguridad, incumplimiento de estas condiciones o uso contrario a la ley.",
        ],
      },
      {
        title: "Integraciones y terceros",
        body: [
          "Al conectar servicios externos, el usuario autoriza a LyPro AI a procesar los datos técnicos necesarios para operar dichas integraciones.",
          "La disponibilidad de algunas funciones depende de proveedores externos. Cambios, revisiones, limitaciones o bloqueos de esos proveedores pueden afectar el servicio.",
        ],
      },
      {
        title: "Inteligencia artificial",
        body: [
          "Las respuestas generadas por IA pueden requerir supervision humana. La empresa usuaria debe revisar y ajustar sus flujos, reglas, datos de negocio y mensajes cuando sea necesario.",
          "LyPro AI no reemplaza asesorias legales, medicas, financieras ni decisiones profesionales especializadas.",
        ],
      },
      {
        title: "Contacto",
        body: [
          `Para preguntas sobre estas condiciones, soporte o solicitudes relacionadas con la cuenta, escribe a ${contactEmail}.`,
        ],
      },
    ],
  },
  "data-deletion": {
    eyebrow: "Datos de usuario",
    title: "Instrucciones para eliminacion de datos",
    updatedAt,
    intro:
      "Esta página explica como un usuario o empresa puede solicitar la eliminacion de datos personales procesados por LyPro AI.",
    icon: <DeleteOutline />,
    sections: [
      {
        title: "Como solicitar la eliminacion",
        body: [
          `Envia un correo a ${contactEmail} con el asunto "Solicitud de eliminacion de datos".`,
          "Incluye el nombre de la empresa asociada, correo de la cuenta, número o canal usado, y una descripción clara de los datos que deseas eliminar.",
          "Si la solicitud corresponde a datos de una conversación, incluye el canal y una referencia que nos permita ubicarla sin compartir información sensible innecesaria.",
        ],
      },
      {
        title: "Validación de identidad",
        body: [
          "Podemos pedir información adicional para confirmar que la persona solicitante esta autorizada a pedir la eliminacion.",
          "Si la solicitud proviene de un usuario final de una empresa cliente, podemos coordinar la eliminacion con dicha empresa cuando sea necesario.",
        ],
      },
      {
        title: "Plazo de respuesta",
        body: [
          "Revisaremos la solicitud y responderemos dentro de un plazo razonable segun la complejidad del caso y la ley aplicable.",
          "Cuando la eliminacion sea procedente, borraremos o anonimizaremos los datos personales en nuestros sistemas activos, salvo información que debamos conservar por motivos legales, seguridad, auditoria o prevencion de fraude.",
        ],
      },
      {
        title: "Datos conectados con Meta",
        body: [
          "Si usaste Facebook, Instagram o WhatsApp para interactuar con una empresa conectada a LyPro AI, puedes solicitar la eliminacion de los datos procesados por LyPro AI siguiendo estas instrucciones.",
          "La eliminacion en LyPro AI no reemplaza las herramientas de privacidad que Meta u otros terceros puedan ofrecer directamente en sus propias plataformas.",
        ],
      },
    ],
  },
};

const pageSx = {
  minHeight: "100vh",
  bgcolor: "#050814",
  color: "text.primary",
  px: { xs: 2, md: 5 },
  py: { xs: 4, md: 7 },
  boxSizing: "border-box",
};

const LegalPage = ({ kind }: { kind: LegalPageKind }) => {
  const content = legalContent[kind];

  return (
    <Box sx={pageSx}>
      <Stack spacing={4} sx={{ width: "100%", maxWidth: 980, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box
            component="img"
            src="/assets/icons/ic-logo-light.png"
            alt="LyPro AI"
            sx={{ width: 140, height: 44, objectFit: "contain", objectPosition: "left center" }}
          />
          <Button
            component={RouterLink}
            to="/"
            color="inherit"
            variant="outlined"
            startIcon={<ArrowBack />}
            sx={{ minHeight: 42 }}
          >
            Volver
          </Button>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.12)",
            bgcolor: "rgba(12, 17, 31, 0.92)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, md: 4 },
              py: { xs: 3, md: 4 },
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              background:
                "linear-gradient(135deg, rgba(56,230,197,0.13), rgba(143,199,255,0.08) 52%, rgba(255,255,255,0.02))",
            }}
          >
            <Stack spacing={2}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  color: "#041014",
                  bgcolor: "#38e6c5",
                }}
              >
                {content.icon}
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "#8ef4de", fontWeight: 900, letterSpacing: 0 }}
                >
                  {content.eyebrow}
                </Typography>
                <Typography variant="h3" sx={{ mt: 0.5, fontWeight: 900 }}>
                  {content.title}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Ultima actualizacion: {content.updatedAt}
                </Typography>
              </Box>
              <Typography sx={{ color: "rgba(255,255,255,0.78)", maxWidth: 820 }}>
                {content.intro}
              </Typography>
            </Stack>
          </Box>

          <Stack spacing={0} divider={<Divider />} sx={{ px: { xs: 2.5, md: 4 } }}>
            {content.sections.map((section) => (
              <Box key={section.title} sx={{ py: { xs: 3, md: 3.5 } }}>
                <Typography variant="h5" sx={{ fontWeight: 900, mb: 1.5 }}>
                  {section.title}
                </Typography>
                <Stack spacing={1.2}>
                  {section.body.map((paragraph) => (
                    <Typography
                      key={paragraph}
                      sx={{ color: "rgba(255,255,255,0.74)", lineHeight: 1.75 }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>

          <Box
            sx={{
              px: { xs: 2.5, md: 4 },
              py: 3,
              borderTop: "1px solid rgba(255,255,255,0.1)",
              bgcolor: "rgba(255,255,255,0.035)",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
              <Email sx={{ color: "#8fc7ff" }} />
              <Typography color="text.secondary">
                Contacto legal:{" "}
                <Box component="a" href={`mailto:${contactEmail}`} sx={{ color: "#8ef4de" }}>
                  {contactEmail}
                </Box>
              </Typography>
            </Stack>
          </Box>
        </Paper>

        <Stack
          component="nav"
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
          sx={{ pb: 2 }}
        >
          <Button component={RouterLink} to="/privacy-policy" color="inherit">
            Politica de privacidad
          </Button>
          <Button component={RouterLink} to="/terms" color="inherit">
            Condiciones
          </Button>
          <Button component={RouterLink} to="/data-deletion" color="inherit">
            Eliminacion de datos
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default LegalPage;
